import React, { createContext, useContext, useReducer, ReactNode } from 'react';

interface AssessmentState {
  currentSessionId: string | null;
  currentPage: number;
  responses: { [questionId: string]: string };
  participantInfo: {
    name: string;
    email: string;
    phone?: string;
    organization?: string;
    position?: string;
    age?: number;
    gender?: string;
    education?: string;
    experience?: string;
  } | null;
  assessmentType: '360' | null;
  isLoading: boolean;
  // New: Track if questionnaire is complete (all questions answered, awaiting participant info)
  questionnaireComplete: boolean;
}

type AssessmentAction =
  | { type: 'SET_SESSION_ID'; payload: string }
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'UPDATE_RESPONSE'; payload: { questionId: string; answer: string } }
  | { type: 'SET_PARTICIPANT_INFO'; payload: AssessmentState['participantInfo'] }
  | { type: 'SET_ASSESSMENT_TYPE'; payload: '360' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'RESET_STATE' }
  | { type: 'CLEAR_PAGE_RESPONSES'; payload: number }
  | { type: 'SET_QUESTIONNAIRE_COMPLETE'; payload: boolean };

const initialState: AssessmentState = {
  currentSessionId: null,
  currentPage: 1,
  responses: {},
  participantInfo: null,
  assessmentType: null,
  isLoading: false,
  questionnaireComplete: false,
};

function assessmentReducer(state: AssessmentState, action: AssessmentAction): AssessmentState {
  switch (action.type) {
    case 'SET_SESSION_ID':
      return { ...state, currentSessionId: action.payload };
    
    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };
    
    case 'UPDATE_RESPONSE':
      return {
        ...state,
        responses: {
          ...state.responses,
          [action.payload.questionId]: action.payload.answer,
        },
      };
    
    case 'SET_PARTICIPANT_INFO':
      return { ...state, participantInfo: action.payload };
    
    case 'SET_ASSESSMENT_TYPE':
      return { ...state, assessmentType: action.payload };
    
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'CLEAR_PAGE_RESPONSES':
      const filteredResponses = Object.keys(state.responses).reduce((acc, key) => {
        if (!key.includes(`_q${action.payload}_`)) {
          acc[key] = state.responses[key];
        }
        return acc;
      }, {} as { [key: string]: string });

      return { ...state, responses: filteredResponses };

    case 'SET_QUESTIONNAIRE_COMPLETE':
      return { ...state, questionnaireComplete: action.payload };

    case 'RESET_STATE':
      return initialState;

    default:
      return state;
  }
}

interface AssessmentContextType {
  state: AssessmentState;
  dispatch: React.Dispatch<AssessmentAction>;
}

const AssessmentContext = createContext<AssessmentContextType | undefined>(undefined);

export function AssessmentProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState);

  return (
    <AssessmentContext.Provider value={{ state, dispatch }}>
      {children}
    </AssessmentContext.Provider>
  );
}

export function useAssessment() {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessment must be used within an AssessmentProvider');
  }
  return context;
}